export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type AutoShowBlockedReason =
  | "missing_owner"
  | "invalid_now"
  | "invalid_last_ts"
  | "within_window";

export type AutoShowDecision =
  | { allowed: true; ageMs: number | null }
  | { allowed: false; reason: AutoShowBlockedReason; ageMs: number | null };

export function normalizeTimestampMs(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) {
    return null;
  }
  const ms = Math.floor(n);
  return ms >= 0 ? ms : null;
}

export function computeAgeMs(
  nowMs: unknown,
  lastShownAtMs: unknown,
): number | null {
  const now = normalizeTimestampMs(nowMs);
  const last = normalizeTimestampMs(lastShownAtMs);
  if (now === null || last === null) {
    return null;
  }
  return Math.max(0, now - last);
}

/**
 * Decide if tracker report is allowed to auto-show given a time window.
 *
 * Pure function: does not read storage, does not mutate.
 */
export function decideTrackerReportAutoShow(params: {
  ownerId: string | null | undefined;
  nowMs: unknown;
  lastShownAtMs: unknown;
  windowMs?: number;
}): AutoShowDecision {
  const {
    ownerId,
    nowMs,
    lastShownAtMs,
    windowMs = TWENTY_FOUR_HOURS_MS,
  } = params;
  if (!ownerId) {
    return { allowed: false, reason: "missing_owner", ageMs: null };
  }

  const now = normalizeTimestampMs(nowMs);
  if (now === null) {
    return { allowed: false, reason: "invalid_now", ageMs: null };
  }

  const last = normalizeTimestampMs(lastShownAtMs);
  const ageMs = last === null ? null : Math.max(0, now - last);

  // No last shown value -> allow.
  if (last === null) {
    return { allowed: true, ageMs: null };
  }

  // Within time window -> block.
  if (ageMs !== null && ageMs < windowMs) {
    return { allowed: false, reason: "within_window", ageMs };
  }

  // Allow at boundary and beyond.
  return { allowed: true, ageMs };
}

export type ClickDedupeDecision =
  | { deduped: true; ageMs: number }
  | { deduped: false; ageMs: number | null };

/**
 * Decide whether a click should be deduped (ignored) within a time window.
 *
 * Pure function: you pass in `nowMs` and `lastClickAtMs`.
 */
export function decideClickDedupe(params: {
  nowMs: unknown;
  lastClickAtMs: unknown;
  windowMs: number;
}): ClickDedupeDecision {
  const { nowMs, lastClickAtMs, windowMs } = params;
  const now = normalizeTimestampMs(nowMs);
  const last = normalizeTimestampMs(lastClickAtMs);
  if (now === null || last === null) {
    return { deduped: false, ageMs: null };
  }
  const ageMs = Math.max(0, now - last);
  return ageMs < windowMs
    ? { deduped: true, ageMs }
    : { deduped: false, ageMs };
}
